import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
} from '@angular/forms';
import { distinctUntilChanged, debounceTime, map } from 'rxjs/operators';
import {
  AddressDetailsTabsComponent,
  CustomerAddressFormBody,
} from '../address-details-tabs/address-details-tabs.component';

import {
  AhoyCustomerService,
  AddressVerificationQuery,
  AddressVerificationResult,
  CustomerDetailsConfigurations,
  CustomerDetailsData,
} from '../ahoy-customer.service';

import {
  AhoyCustomerInteractionService,
  LatLngCoordinates,
} from '../ahoy-customer-interaction.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';

/** A single address result as returned by the places/geocoding lookups. */
interface AddressResultData {
  id?: string | number;
  title?: string;
  formatted_address?: string;
  area?: string;
  name?: string;
  [key: string]: unknown;
}

/** Standard response wrapper `{ data: [...] }`, or a flat AddressResultData. */
interface AddressLookupResponse extends AddressResultData {
  data?: AddressResultData[];
}

interface ValidateAddressResult {
  status?: number | string;
  // Typed `unknown` rather than `boolean`: `res?.ok && res.ok === false` below
  // is a pre-existing no-op check (if `ok` is truthy it can never also be
  // `=== false`), so a `boolean` type makes TS flag the comparison as
  // unreachable. Left as-is to avoid changing behavior; see report.
  ok?: unknown;
  validityType?: number;
  [key: string]: unknown;
}

interface SelectedAddressResult {
  id?: string | number;
  result?: { position?: LatLngCoordinates };
  position?: LatLngCoordinates;
  [key: string]: unknown;
}

interface LocationTag {
  id: number;
  name: string;
  icon: string;
}

@Component({
  selector: 'app-new-customer-details',
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss'],
  standalone: false,
})
export class CustomerDetailsComponent implements OnInit, AfterViewInit {
  isLoading = false;
  invalidLatLonError = '';
  guestToken = '';
  NationalAddressRequired = environment.countrySettings.nationalAddress;

  @ViewChild('mapContainer') mapContainer: ElementRef;
  @ViewChild('addressTabs') addressTabs: AddressDetailsTabsComponent;

  @Input() isExpress = false;
  @Input('configurations') configurations: CustomerDetailsConfigurations = {
    callFindCustomerAddressByPhone: true,
    defaultPhoneCode: '',
    hideCustomerName: false,
    hideContactCustomer: false,
    hideAddressName: true,
    hideTagLocationName: true,
    validatePlaces: false,
    disablePhoneField: false,
    initialLatitude: 25.2048,
    initialLongitude: 55.2708,
    validateAddress: false,
    departureAirportId: 0,
    arrivalAirportId: 0,
    phoneNumber: '',
  };
  @Input() showSaveToAddressBook = false;
  @Output('onLocationChange') onLocationChange = new EventEmitter<boolean>();

  saveToAddressBook = false;
  enteredQuery: string;
  htmlTag: HTMLHtmlElement;
  customerFormGroup: FormGroup;
  addresses: AddressResultData[];
  map: H.Map;
  marker: H.map.Marker;
  behavior: H.mapevents.Behavior;
  isLocationValid: boolean = true;
  selectedTagName: string = '';
  // the initial coordinates where the map
  // and the marker will show when the page first load
  private initialLatitude = ''; //25.2048;
  private initialLongitude = ''; //55.2708;
  countryCode = '+' + environment.countrySettings.countryCode;
  validatePlaces: boolean;
  isValidButRemote: boolean = false;
  locationValidationMessage: string;
  direction: string;
  tagLocationName: Array<LocationTag> = [
    { id: 1, name: 'Home', icon: 'location_on' },
    { id: 2, name: 'Work', icon: 'location_on' },
    { id: 3, name: 'Other', icon: '' },
  ];

  constructor(
    private _formBuilder: FormBuilder,
    private ahoyCustomerService: AhoyCustomerService,
    private cdr: ChangeDetectorRef,
    private ahoyCustomerInteractionService: AhoyCustomerInteractionService,
    private translateService: TranslateService,
  ) {
    this.handleCustomerInteractionCall();
  }

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setForm();
    this.cdr.detectChanges();
    this.forceUpdateHtmlDirection();
    if (this.configurations.validateAddress) {
      this.setLatLonInvalid();
      this.applyLatLonValidation();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // setup here map
      this.setUpHereMap();
    }, 1000);
  }

  setLatLonInvalid() {
    setTimeout(() => {
      const CustomerLatitude = this.customerFormGroup.get('CustomerLatitude');
      const CustomerLongitude = this.customerFormGroup.get('CustomerLongitude');
      CustomerLatitude?.setErrors({ invalid: true });
      CustomerLongitude?.setErrors({ invalid: true });
      CustomerLatitude?.markAsTouched();
      CustomerLongitude?.markAsTouched();
    }, 200);
  }

  applyLatLonValidation() {
    const latControl = this.customerFormGroup.get('CustomerLatitude');
    const lngControl = this.customerFormGroup.get('CustomerLongitude');

    this.customerFormGroup.valueChanges
      .pipe(
        debounceTime(300),
        map(() => ({ lat: latControl?.value, lng: lngControl?.value })),
        distinctUntilChanged((a, b) => a.lat === b.lat && a.lng === b.lng),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.addressVerification());
  }

  addressVerification() {
    const latitude = this.customerFormGroup.get('CustomerLatitude')?.value;
    const longitude = this.customerFormGroup.get('CustomerLongitude')?.value;

    if (latitude && longitude) {
      let queryParam: AddressVerificationQuery = {
        latitude,
        longitude,
      };

      if (this.configurations.departureAirportId) {
        queryParam.departureAirportId = this.configurations.departureAirportId;
      } else if (this.configurations.arrivalAirportId) {
        queryParam.arrivalAirportId = this.configurations.arrivalAirportId;
      }

      this.ahoyCustomerService
        .addressVerification(queryParam, this.guestToken || undefined)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: ({ isValid, message }: AddressVerificationResult) => {
            if (isValid) {
              this.customerFormGroup.get('CustomerLatitude')?.setErrors(null);
              this.customerFormGroup.get('CustomerLongitude')?.setErrors(null);
              this.invalidLatLonError = '';
            } else {
              this.setLatLonInvalid();
              this.customerFormGroup.get('CustomerLatitude')?.markAsTouched();
              this.customerFormGroup.get('CustomerLongitude')?.markAsTouched();
              this.invalidLatLonError = message;
            }
            // check and update validations and erros here
            // this.customerFormGroup.
            this.customerFormGroup.updateValueAndValidity();
          },
          error: () => this.setLatLonInvalid(),
        });
    }
  }

  bindCustomer(
    customer: CustomerDetailsData,
    configurations: CustomerDetailsConfigurations,
  ) {
    this.validatePlaces = configurations.validatePlaces;

    if (configurations.disablePhoneField) {
      this.customerFormGroup.controls['CustomerPhone'].disable();
    }

    this.customerFormGroup.get('CustomerName')!.setValue(customer.customerName);
    this.customerFormGroup.get('AddressName')!.setValue(customer.addressName);
    if (customer.customerPhone) {
      this.customerFormGroup
        .get('CustomerPhone')!
        .setValue(customer.customerPhone, { emitEvent: false });
    }

    if (customer.nationalAddress) {
      this.customerFormGroup
        .get('nationalAddress')
        .setValue(customer.nationalAddress);
    }

    this.customerFormGroup
      .get('DontContactCustomer')!
      .setValue(
        customer.dontContactCustomer ? customer.dontContactCustomer : false,
      );

    this.customerFormGroup.get('CustomerAddress')!.setValue(
      {
        title: customer.customerAddress,
      },
      { emitEvent: false },
    );

    const lat = customer.customerLocationLatitude ?? customer.customerLatitude;
    const lng =
      customer.customerLocationLongitude ?? customer.customerLongitude;

    if (lat && lng) {
      this.updateCustomerLatLng(lat, lng);
    }
    this.addressTabs.bindAddressInformation(customer);

    this.bindTagLocationName(customer);
  }

  private updateCustomerLatLng(lat: string | number, lng: string | number) {
    this.customerFormGroup.get('CustomerLatitude')!.setValue(lat);
    this.customerFormGroup.get('CustomerLongitude')!.setValue(lng);
    this.setMarkerLocation(lat, lng);
    if (this.validatePlaces) {
      this.customerFormGroup.setErrors({ invalid: true });
      this.validateCustomerLocation(+lat, +lng);
    }
    this.addressVerification();
  }

  /**
   * determine whether the data is valid or not
   * @param touchForm whether to mark the form as touched
   */
  isValid(touchForm = true): boolean {
    if (touchForm) {
      this.customerFormGroup.markAllAsTouched();
    }
    // run isValid on all
    const addressValid = this.addressTabs?.isValid(touchForm);
    const isValid =
      this.customerFormGroup.valid &&
      addressValid &&
      (this.isLocationValid || !this.validatePlaces);

    return isValid;
  }

  /**
   * validate the address string from google api
   * and set the address input to the fetched string
   * @param lat latitude number
   * @param lng longitude number
   */
  validateCustomerLocation(lat: number | string, lng: number | string) {
    this.locationValidationMessage = '';
    this.ahoyCustomerInteractionService.sendValidateAddressByLatLng(lat, lng);
  }

  addressSelected() {
    // this.map.removeObject(this.marker);
    const address = this.customerFormGroup.get('CustomerAddress')?.value;

    this.customerFormGroup.setErrors({ invalid: true });
    this.addressTabs.setAreaValue(address.title);

    this.ahoyCustomerInteractionService.sendSelectedAddress(address.id);
  }

  getValues(): CustomerAddressFormBody {
    let basicInfo = {};

    const geometry = this.marker.getGeometry();
    const body: CustomerAddressFormBody = {
      ...this.customerFormGroup.getRawValue(),
      ...basicInfo,
      CustomerLatitude:
        geometry.lat || this.marker.a?.lat || this.marker.$l?.lat,
      CustomerLongitude:
        geometry.lng || this.marker.a?.lng || this.marker.$l?.lng,
    };

    // CustomerPhone is already the combined value from ahoy-phone-input
    if (body.DeliveryDateTime) {
      body.DeliveryDateTime = (body.DeliveryDateTime as Date).getTime();
    }
    if (body.CustomerAddress instanceof Object) {
      body.CustomerAddress = body.CustomerAddress.title;
    }

    this.addressTabs.updateBodyValues(body);
    this.updateTagLocationNameValue(body);
    return body;
  }

  private setForm() {
    this.customerFormGroup = this._formBuilder.group({
      CustomerName: [
        '',
        this.configurations?.hideCustomerName
          ? Validators.nullValidator
          : Validators.required,
      ],
      AddressName: [
        '',
        this.configurations?.hideAddressName
          ? Validators.nullValidator
          : Validators.required,
      ],
      CustomerPhone: ['', Validators.required],
      DontContactCustomer: [false],
      CustomerAddress: [''],
      CustomerLatitude: [
        this.configurations.initialLatitude,
        Validators.required,
      ],
      CustomerLongitude: [
        this.configurations.initialLongitude,
        Validators.required,
      ],
      nationalAddress: [
        '',
        this.NationalAddressRequired
          ? [Validators.required, Validators.pattern(/^[A-Za-z]{4}[0-9]{4}$/)]
          : [],
      ],
      CityId: [],
      CommunityId: [],
      locationNameFormGroup: this._formBuilder.group({
        locationName: new FormControl(),
      }),
      customLocationName: [
        '',
        this.configurations?.hideTagLocationName &&
        this.selectedTagName == 'Other'
          ? [Validators.nullValidator]
          : [Validators.required, Validators.maxLength(20)],
      ],
    });

    if (this.configurations.phoneNumber) {
      this.customerFormGroup
        .get('CustomerPhone')
        .setValue(this.configurations.phoneNumber);
    } else {
      this.customerFormGroup
        .get('CustomerPhone')
        .setValue(this.configurations.defaultPhoneCode || this.countryCode);
    }

    // this.cdr.detectChanges();
    const latLngValueChange = () => {
      const lat = this.customerFormGroup.get('CustomerLatitude')?.value;
      const lng = this.customerFormGroup.get('CustomerLongitude')?.value;
      this.latLngChanged(lat, lng);
    };

    // subscribe to customer lat, lng changes to set the address and the marker on the map
    this.customerFormGroup
      .get('CustomerLatitude')!
      .valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(latLngValueChange);

    this.customerFormGroup
      .get('CustomerLongitude')!
      .valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(latLngValueChange);

    // listen to google maps address change to fill the dropdown list
    this.customerFormGroup
      .get('CustomerAddress')!
      .valueChanges.pipe(
        debounceTime(1000),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((val) => {
        if (typeof val === 'string') {
          this.enteredQuery = val;
          if (val.trim()) {
            this.getPlacesAutoComplete();
          } else {
            this.addresses = [];
          }
          // Typing doesn't move the map: reverse-geocoding the map centre here
          // would overwrite what the user is typing. Picking a suggestion updates
          // the lat/lng controls, whose own subscriptions refresh the address.
          return;
        }

        if (val.id) {
          this.handleSelectedAddress(val);
        }
      });

    this.customerFormGroup
      .get('CustomerPhone')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (this.customerFormGroup.get('CustomerPhone')!.valid && value) {
          if (this.configurations.callFindCustomerAddressByPhone) {
            this.loadCustomerInformation(value.replace('+', ''));
          }
        }
      });
  }

  /**
   * this method load the customer information
   * based on the phone number if it exists
   * @param phone customer phone number
   */
  private loadCustomerInformation(phone: string) {
    this.ahoyCustomerService
      .getCustomerInformationByPhone(`${phone}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.bindCustomerDetails(res);
        },
        (err) => {
          // no need to show error message,
          // error here means the customer doesn't exit
          // this.showErrorDialog(err);
        },
      );
  }

  private latLngChanged(lat: string, lng: string) {
    // call the places service to get the address

    this.customerFormGroup.setErrors({ invalid: true });
    this.setAddressStringByCoords(+lat, +lng);
    this.setMarkerLocation(lat, lng);
    this.validateCustomerLocation(+lat, +lng);
    this.customerFormGroup.updateValueAndValidity();
  }

  private getPlacesAutoComplete() {
    let autocompleteQuery = {
      query: encodeURI(this.enteredQuery),
    };
    this.ahoyCustomerInteractionService.sendAutoCompleteQuery(
      autocompleteQuery,
    );
  }

  /**
   * bind the form to the customer information based on customer phone number response
   * @param data customer data based on the API response
   */
  private bindCustomerDetails(data: CustomerDetailsData) {
    const name = `${data.customerName}`;
    this.customerFormGroup.get('CustomerName')!.setValue(name);

    this.customerFormGroup.get('CustomerAddress')!.setValue({
      title: data.customerAddress,
    });
    this.customerFormGroup
      .get('CustomerLatitude')!
      .setValue(data.customerLocationLatitude, { emitEvent: false });
    this.customerFormGroup
      .get('CustomerLongitude')!
      .setValue(data.customerLocationLongitude, { emitEvent: false });
    this.setMarkerLocation(
      data.customerLocationLatitude,
      data.customerLocationLongitude,
    );
    this.validateCustomerLocation(
      data.customerLocationLatitude,
      data.customerLocationLongitude,
    );

    this.addressTabs.bindAddressInformation(data);
  }

  setCustomerPhone(phone: string, code = this.countryCode) {
    this.customerFormGroup
      .get('CustomerPhone')!
      .setValue(`${code}${phone}`, { emitEvent: false });
  }

  /**
   * setting up here map with satalite settings and events
   */
  setUpHereMap() {
    const platform = new H.service.Platform({
      apikey: environment.here_maps_key,
    });
    const defaultLayers = platform.createDefaultLayers();
    this.mapContainer.nativeElement.innerHTML = '';
    this.map = new H.Map(
      this.mapContainer.nativeElement,
      defaultLayers.vector.normal.map,
      {
        zoom: 15,
        center: { lat: 25.2048, lng: 55.2708 },
      },
    );
    const mapEvents = new H.mapevents.MapEvents(this.map);
    this.behavior = new H.mapevents.Behavior(mapEvents);
    // add map tab event.
    this.map.addEventListener('tap', (ev: H.HereMapEvent) => {
      // get the coordinates from the map
      const coord = this.map.screenToGeo(
        ev.currentPointer.viewportX,
        ev.currentPointer.viewportY,
      );
      // set the marker location based on the coordinates extracted from the map
      this.marker.setGeometry(coord);
      // find the address of the new coordinates and set the adddress string input to it.
      this.setAddressByCoords(coord.lat, coord.lng);
    });

    H.ui.UI.createDefault(this.map, defaultLayers);

    this.marker = this.addMarker(
      this.map,
      {
        lat: this.configurations.initialLatitude,
        lng: this.configurations.initialLongitude,
      },
      this.behavior,
    );
  }

  /**
   * set the map marker location to the given lat, lng
   * @param lat latitude where the marker should be placed
   * @param lng longitude where the marker should be placed
   */
  private setMarkerLocation(lat: string | number, lng: string | number) {
    this.map.removeObject(this.marker);
    this.marker = this.addMarker(this.map, { lat, lng }, this.behavior);
  }

  /**
   * get the address string from google api
   * and set the address input to the fetched string
   * @param lat latitude number
   * @param lng longitude number
   */
  private setAddressByCoords(lat: number, lng: number) {
    this.onLocationChange.emit(this.isLocationValid);
    // set the lat, lng fields
    this.customerFormGroup.get('CustomerLatitude')!.setValue(lat);
    this.customerFormGroup.get('CustomerLongitude')!.setValue(lng);
    this.customerFormGroup.setErrors({ invalid: true });
    this.setAddressStringByCoords(lat, lng);
    this.validateCustomerLocation(lat, lng);
    this.addressVerification();
  }

  private setAddressStringByCoords(lat: number, lng: number) {
    // call the places service to get the address

    this.ahoyCustomerInteractionService.sendAdressByLatLng(+lat, +lng);
  }

  /**
   * Add marker on here map object in
   * the coordinates specified
   * and with the same behavior
   * @param map here map object
   * @param coords coordinates latitude & longitude
   * @param behavior here map behavior object
   */
  private addMarker(
    map: H.Map,
    coords: { lat: number | string; lng: number | string },
    behavior: H.mapevents.Behavior,
  ): H.map.Marker {
    const marker = new H.map.Marker(coords, {
      volatility: true,
    });
    marker.draggable = true;
    map.addObject(marker);
    map.setCenter(coords);

    // drag started event
    map.addEventListener(
      'dragstart',
      (ev: H.HereMapEvent) => {
        const target = ev.target;
        const pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          const targetPosition = map.geoToScreen(target.getGeometry());
          target.offset = new H.math.Point(
            pointer.viewportX - targetPosition.x,
            pointer.viewportY - targetPosition.y,
          );
          behavior.disable();
        }
      },
      false,
    );

    map.addEventListener(
      'dragend',
      (ev: H.HereMapEvent) => {
        const target = ev.target;
        if (target instanceof H.map.Marker) {
          behavior.enable();
          // get the coordinates from the map
          const coord = this.map.screenToGeo(
            ev.currentPointer.viewportX,
            ev.currentPointer.viewportY,
          );
          // set the marker location based on the coordinates extracted from the map
          this.marker.setGeometry(coord);
          // find the address of the new coordinates and set the adddress string input to it.
          this.setAddressByCoords(coord.lat, coord.lng);
        }
      },
      false,
    );

    // Listen to the drag event and move the position of the marker
    // as necessary
    map.addEventListener(
      'drag',
      (ev: H.HereMapEvent) => {
        const target = ev.target;
        const pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          target.setGeometry(
            map.screenToGeo(
              pointer.viewportX - target.offset.x,
              pointer.viewportY - target.offset.y,
            ),
          );
        }
      },
      false,
    );

    return marker;
  }

  /**
   * display helper to get the displayed string on the address auto complete dropdown
   * @param address google maps address object
   */
  displayAddress(address: AddressResultData) {
    return address.title;
  }
  handleAddressStringChangeRes(res: AddressLookupResponse) {
    // Handle standard response wrapper { data: [...] } or flat object
    const location = res?.data ? res.data[0] : res;

    if (location) {
      const addressTitle =
        location.title ||
        location.formatted_address ||
        location.area ||
        location.name ||
        '';

      if (addressTitle) {
        // Pass { emitEvent: false } here to stop the infinite loop
        this.customerFormGroup.get('CustomerAddress')!.setValue(
          {
            title: addressTitle,
            ...location,
          },
          { emitEvent: false },
        );
        this.addressTabs?.setAreaValue?.(addressTitle);
        this.marker;
      }
    }
  }
  handleValidateAddress(res: ValidateAddressResult): void {
    if (this.configurations.validateAddress) return;
    this.customerFormGroup.get('CustomerLatitude')?.markAsTouched();
    this.customerFormGroup.get('CustomerLongitude')?.markAsTouched();
    if (res?.ok && res.ok === false) {
      this.isLocationValid = false;
      this.locationValidationMessage = this.translateService.instant(
        "We couldn't verify this location. Please choose a different address.",
      );
    } else {
      if (res.validityType === 1 || res.validityType === 2) {
        this.isLocationValid = true;
        this.isValidButRemote = false;
        this.customerFormGroup.get('CustomerLatitude')?.setErrors(null);
        this.customerFormGroup.get('CustomerLongitude')?.setErrors(null);
        this.customerFormGroup?.setErrors(null);
      }

      if (res.validityType === 2) {
        this.isLocationValid = true;
        this.isValidButRemote = true;
      }

      if (res.validityType === 3) {
        this.isLocationValid = false;
        this.isValidButRemote = false;
        this.customerFormGroup
          .get('CustomerLatitude')
          ?.setErrors({ invalid: true });
        this.customerFormGroup
          .get('CustomerLongitude')
          ?.setErrors({ invalid: true });
      }

      this.locationValidationMessage = this.getLocationValidationMessage(
        res.validityType,
      );
    }
  }

  /**
   * The backend's raw validation message isn't customer-friendly (it can
   * describe internal zone/airport-matching logic), so it's never shown
   * directly — only `validityType` drives what the customer sees.
   */
  private getLocationValidationMessage(validityType: number): string {
    switch (validityType) {
      case 2:
        return this.translateService.instant(
          'This address is outside our standard delivery area, but we can still deliver here.',
        );
      case 3:
        return this.translateService.instant(
          "This address doesn't seem to be near the selected airport. Please choose a location closer to it.",
        );
      default:
        return '';
    }
  }

  handleAutoComplete(res: AddressResultData[]) {
    this.addresses = res;
  }

  updateMapCenter(lat: number, lng: number) {
    if (this.map) {
      const newLatLng = { lat, lng };
      this.map.setCenter(newLatLng);
      this.map.setZoom(16);

      if (this.marker) {
        this.marker.setGeometry(newLatLng);
      } else {
        // Fallback if marker hasn't been initialized yet
        this.marker = this.addMarker(this.map, newLatLng, this.behavior);
      }
    }
  }

  handleSelectedAddress(res: SelectedAddressResult) {
    if (res.result && res.result.position) {
      const loc = res.result.position;
      this.updateCustomerLatLng(loc.lat, loc.lng);
    } else if (res && res.position) {
      const loc = res.position;
      this.updateCustomerLatLng(loc.lat, loc.lng);
    }
  }

  handleCustomerInteractionCall() {
    this.ahoyCustomerInteractionService
      .getAddressRes()
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res) {
          this.handleAddressStringChangeRes(res as AddressLookupResponse);
        }
      });

    this.ahoyCustomerInteractionService
      .getValidateAddressRes()
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const validateRes = res as ValidateAddressResult;
        if (
          validateRes?.status !== 200 &&
          validateRes?.status !== undefined &&
          validateRes?.status !== null &&
          validateRes?.status !== 'OK'
        ) {
          this.isLocationValid = false;
          this.locationValidationMessage = this.translateService.instant(
            "We couldn't verify this location. Please choose a different address.",
          );
        } else {
          this.handleValidateAddress(validateRes);
        }
      });

    this.ahoyCustomerInteractionService
      .getAutoCompleteRes()
      .pipe(
        map((data) => {
          const wrapped = data as { data?: AddressResultData[] };
          return wrapped?.data || (data as AddressResultData[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.handleAutoComplete(res);
      });

    this.ahoyCustomerInteractionService
      .getSelectedAddressRes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        const selectedRes = res as SelectedAddressResult;
        if (selectedRes.id || selectedRes.id === 0) {
          this.handleSelectedAddress(selectedRes);
        }
      });
  }

  forceUpdateHtmlDirection() {
    if (localStorage.getItem('html-dir')) {
      this.direction = localStorage.getItem('html-dir')!;
    }
  }

  onLocationNameTagChange(selectedTag: string) {
    const locationNameControl =
      this.customerFormGroup.get('customLocationName');
    if (
      [this.tagLocationName[0].name, this.tagLocationName[1].name].includes(
        selectedTag,
      )
    ) {
      locationNameControl!.setValue('');
      locationNameControl!.setValidators([]);
    } else {
      locationNameControl!.setValidators([
        Validators.required,
        Validators.maxLength(20),
      ]);
    }
    this.selectedTagName = selectedTag;
    locationNameControl!.updateValueAndValidity();
  }

  bindTagLocationName(customerDetails: CustomerDetailsData) {
    // if tag location name is custom - i.e. not home or work
    if (
      ![this.tagLocationName[0].name, this.tagLocationName[1].name].includes(
        customerDetails.tagLocationName,
      )
    ) {
      this.selectedTagName = this.tagLocationName[2].name;
      this.customerFormGroup
        .get('customLocationName')!
        .setValue(customerDetails.tagLocationName);
    } else {
      this.selectedTagName = customerDetails.tagLocationName;
      this.customerFormGroup.controls['locationNameFormGroup']
        .get('locationName')!
        .setValue(customerDetails.tagLocationName);
    }
    this.onLocationNameTagChange(customerDetails.tagLocationName);
  }

  updateTagLocationNameValue(body: CustomerAddressFormBody) {
    const otherLocationName =
      this.customerFormGroup.get('customLocationName')?.value;
    body.tagLocationName =
      this.selectedTagName == 'Other'
        ? otherLocationName
        : this.selectedTagName;
  }
}
